import * as cv from 'class-validator';
import { ArrayUniqueIdentifier } from 'class-validator/types/decorator/array/ArrayUnique';
import { ValidationOptions } from 'class-validator/types/decorator/ValidationOptions';
import * as ValidatorJS from 'validator';

import { translateErrorKey } from '../i18n/request-translate';

// Wrapper functions for class-validator decorators with i18n support

export type TI18nValidationOptions = Omit<cv.ValidationOptions, 'message'> & {
  messageKey?: string;
};

export function IsUrl(
  options: ValidatorJS.IsURLOptions,
  validationOptions?: TI18nValidationOptions,
): PropertyDecorator {
  const key = validationOptions?.messageKey || 'validation.isUrl';
  const message = () => translateErrorKey(key as never);
  if (validationOptions?.messageKey)
    message.key = validationOptions?.messageKey;
  return cv.IsUrl(options, {
    ...validationOptions,
    message,
    groups: validationOptions?.groups || ['error'],
  });
}

export function IsString(
  validationOptions?: TI18nValidationOptions,
): PropertyDecorator {
  const key = validationOptions?.messageKey || 'validation.isString';
  const message = () => translateErrorKey(key as never);
  if (validationOptions?.messageKey)
    message.key = validationOptions?.messageKey;
  return cv.IsString({
    ...validationOptions,
    message,
    groups: validationOptions?.groups || ['error'],
  });
}

export function IsOptional(
  validationOptions?: TI18nValidationOptions,
): PropertyDecorator {
  const key = validationOptions?.messageKey || 'validation.isOptional';
  const message = () => translateErrorKey(key as never);
  if (validationOptions?.messageKey)
    message.key = validationOptions?.messageKey;
  return cv.IsOptional({
    ...validationOptions,
    message,
    groups: validationOptions?.groups || ['error'],
  });
}

export function IsBoolean(
  validationOptions?: TI18nValidationOptions,
): PropertyDecorator {
  const key = validationOptions?.messageKey || 'validation.isBoolean';
  const message = () => translateErrorKey(key as never);
  if (validationOptions?.messageKey)
    message.key = validationOptions?.messageKey;
  return cv.IsBoolean({
    ...validationOptions,
    message,
    groups: validationOptions?.groups || ['error'],
  });
}

export function IsEnum(
  entity: object,
  validationOptions?: TI18nValidationOptions,
): PropertyDecorator {
  const key = validationOptions?.messageKey || 'validation.isEnum';
  const message = () => translateErrorKey(key as never);
  if (validationOptions?.messageKey)
    message.key = validationOptions?.messageKey;
  return cv.IsEnum(entity, {
    ...validationOptions,
    message,
    groups: validationOptions?.groups || ['error'],
  });
}

export function ValidateNested(
  validationOptions?: TI18nValidationOptions,
): PropertyDecorator {
  const key = validationOptions?.messageKey || 'validation.validateNested';
  const message = () => translateErrorKey(key as never);
  if (validationOptions?.messageKey)
    message.key = validationOptions?.messageKey;
  return cv.ValidateNested({
    ...validationOptions,
    message,
    groups: validationOptions?.groups || ['error'],
  });
}

export function IsNumber(
  options?: cv.IsNumberOptions,
  validationOptions?: TI18nValidationOptions,
): PropertyDecorator {
  const key = validationOptions?.messageKey || 'validation.isNumber';
  const message = () => translateErrorKey(key as never);
  if (validationOptions?.messageKey)
    message.key = validationOptions?.messageKey;
  return cv.IsNumber(options, {
    ...validationOptions,
    message,
    groups: validationOptions?.groups || ['error'],
  });
}

export function IsDate(
  validationOptions?: TI18nValidationOptions,
): PropertyDecorator {
  const key = validationOptions?.messageKey || 'validation.isDate';
  const message = () => translateErrorKey(key as never);
  if (validationOptions?.messageKey)
    message.key = validationOptions?.messageKey;
  return cv.IsDate({
    ...validationOptions,
    message,
    groups: validationOptions?.groups || ['error'],
  });
}

export function IsArray(
  validationOptions?: TI18nValidationOptions,
): PropertyDecorator {
  const key = validationOptions?.messageKey || 'validation.isArray';
  const message = () => translateErrorKey(key as never);
  if (validationOptions?.messageKey)
    message.key = validationOptions?.messageKey;
  return cv.IsArray({
    ...validationOptions,
    message,
    groups: validationOptions?.groups || ['error'],
  });
}

export function IsObject(
  validationOptions?: TI18nValidationOptions,
): PropertyDecorator {
  const key = validationOptions?.messageKey || 'validation.isObject';
  const message = () => translateErrorKey(key as never);
  if (validationOptions?.messageKey)
    message.key = validationOptions?.messageKey;
  return cv.IsObject({
    ...validationOptions,
    message,
    groups: validationOptions?.groups || ['error'],
  });
}

export function ArrayMaxSize(
  max: number,
  validationOptions?: TI18nValidationOptions,
): PropertyDecorator {
  const key = validationOptions?.messageKey || 'validation.arrayMaxSize';
  const message = () => translateErrorKey(key as never, { max });
  if (validationOptions?.messageKey)
    message.key = validationOptions?.messageKey;
  return cv.ArrayMaxSize(max, {
    ...validationOptions,
    message,
    groups: validationOptions?.groups || ['error'],
  });
}

export function IsNotEmpty(
  validationOptions?: TI18nValidationOptions,
): PropertyDecorator {
  const key = validationOptions?.messageKey || 'validation.isNotEmpty';
  const message = () => translateErrorKey(key as never);
  if (validationOptions?.messageKey)
    message.key = validationOptions?.messageKey;
  return cv.IsNotEmpty({
    ...validationOptions,
    message,
    groups: validationOptions?.groups || ['error'],
  });
}

export function MaxLength(
  max: number,
  validationOptions?: TI18nValidationOptions,
): PropertyDecorator {
  const key = validationOptions?.messageKey || 'validation.maxLength';
  const message = () => translateErrorKey(key as never, { max });
  if (validationOptions?.messageKey)
    message.key = validationOptions?.messageKey;
  return cv.MaxLength(max, {
    ...validationOptions,
    message,
    groups: validationOptions?.groups || ['error'],
  });
}

export function Length(
  min: number,
  max: number,
  validationOptions?: TI18nValidationOptions,
): PropertyDecorator {
  const key = validationOptions?.messageKey || 'validation.length';
  const message = () => translateErrorKey(key as never, { min, max });
  if (validationOptions?.messageKey)
    message.key = validationOptions?.messageKey;
  return cv.Length(min, max, {
    ...validationOptions,
    message,
    groups: validationOptions?.groups || ['error'],
  });
}

export function IsUUID(
  version?: ValidatorJS.UUIDVersion,
  validationOptions?: TI18nValidationOptions,
): PropertyDecorator {
  const key = validationOptions?.messageKey || 'validation.isUUID';
  const message = () => translateErrorKey(key as never);
  if (validationOptions?.messageKey)
    message.key = validationOptions?.messageKey;
  return cv.IsUUID(version, {
    ...validationOptions,
    message,
    groups: validationOptions?.groups || ['error'],
  });
}

export function ArrayMinSize(
  min: number,
  validationOptions?: TI18nValidationOptions,
): PropertyDecorator {
  const key = validationOptions?.messageKey || 'validation.arrayMinSize';
  const message = () => translateErrorKey(key as never, { min });
  if (validationOptions?.messageKey)
    message.key = validationOptions?.messageKey;
  return cv.ArrayMinSize(min, {
    ...validationOptions,
    message,
    groups: validationOptions?.groups || ['error'],
  });
}

export function Min(
  minValue: number,
  validationOptions?: TI18nValidationOptions,
): PropertyDecorator {
  const key = validationOptions?.messageKey || 'validation.min';
  const message = () => translateErrorKey(key as never, { min: minValue });
  if (validationOptions?.messageKey)
    message.key = validationOptions?.messageKey;
  return cv.Min(minValue, {
    ...validationOptions,
    message,
    groups: validationOptions?.groups || ['error'],
  });
}

export function Max(
  maxValue: number,
  validationOptions?: TI18nValidationOptions,
): PropertyDecorator {
  const key = validationOptions?.messageKey || 'validation.max';
  const message = () => translateErrorKey(key as never, { max: maxValue });
  if (validationOptions?.messageKey)
    message.key = validationOptions?.messageKey;
  return cv.Max(maxValue, {
    ...validationOptions,
    message,
    groups: validationOptions?.groups || ['error'],
  });
}

export function Equals(
  comparison: unknown,
  validationOptions?: TI18nValidationOptions,
): PropertyDecorator {
  const key = validationOptions?.messageKey || 'validation.equals';
  const message = () => translateErrorKey(key as never, { value: comparison });
  if (validationOptions?.messageKey)
    message.key = validationOptions?.messageKey;
  return cv.Equals(comparison, {
    ...validationOptions,
    message,
    groups: validationOptions?.groups || ['error'],
  });
}

export function NotEquals(
  comparison: unknown,
  validationOptions?: TI18nValidationOptions,
): PropertyDecorator {
  const key = validationOptions?.messageKey || 'validation.notEquals';
  const message = () => translateErrorKey(key as never, { value: comparison });
  if (validationOptions?.messageKey)
    message.key = validationOptions?.messageKey;
  return cv.NotEquals(comparison, {
    ...validationOptions,
    message,
    groups: validationOptions?.groups || ['error'],
  });
}

export function IsDecimal(
  options?: ValidatorJS.IsDecimalOptions,
  validationOptions?: TI18nValidationOptions,
): PropertyDecorator {
  const key = validationOptions?.messageKey || 'validation.isDecimal';
  const message = () => translateErrorKey(key as never);
  if (validationOptions?.messageKey)
    message.key = validationOptions?.messageKey;
  return cv.IsDecimal(options, {
    ...validationOptions,
    message,
    groups: validationOptions?.groups || ['error'],
  });
}

export function ArrayUnique<T = unknown>(
  identifierOrOptions?: ArrayUniqueIdentifier<T> | ValidationOptions,
  validationOptions?: TI18nValidationOptions,
): PropertyDecorator {
  const key = validationOptions?.messageKey || 'validation.arrayUnique';
  const message = () => translateErrorKey(key as never);
  if (validationOptions?.messageKey)
    message.key = validationOptions?.messageKey;
  return cv.ArrayUnique(identifierOrOptions, {
    ...validationOptions,
    message,
    groups: validationOptions?.groups || ['error'],
  });
}

export function IsEmail(
  options?: ValidatorJS.IsEmailOptions,
  validationOptions?: TI18nValidationOptions,
): PropertyDecorator {
  const key = validationOptions?.messageKey || 'validation.isEmail';
  const message = () => translateErrorKey(key as never);
  if (validationOptions?.messageKey)
    message.key = validationOptions?.messageKey;
  return cv.IsEmail(options, {
    ...validationOptions,
    message,
    groups: validationOptions?.groups || ['error'],
  });
}

export function IsNumberString(
  options?: ValidatorJS.IsNumericOptions,
  validationOptions?: TI18nValidationOptions,
): PropertyDecorator {
  const key = validationOptions?.messageKey || 'validation.isNumberString';
  const message = () => translateErrorKey(key as never);
  if (validationOptions?.messageKey)
    message.key = validationOptions?.messageKey;
  return cv.IsNumberString(options, {
    ...validationOptions,
    message,
    groups: validationOptions?.groups || ['error'],
  });
}

export function IsFQDN(
  options?: ValidatorJS.IsFQDNOptions,
  validationOptions?: TI18nValidationOptions,
): PropertyDecorator {
  const key = validationOptions?.messageKey || 'validation.isFQDN';
  const message = () => translateErrorKey(key as never);
  if (validationOptions?.messageKey)
    message.key = validationOptions?.messageKey;
  return cv.IsFQDN(options, {
    ...validationOptions,
    message,
    groups: validationOptions?.groups || ['error'],
  });
}

export function IsInt(
  validationOptions?: TI18nValidationOptions,
): PropertyDecorator {
  const key = validationOptions?.messageKey || 'validation.isInt';
  const message = () => translateErrorKey(key as never);
  if (validationOptions?.messageKey)
    message.key = validationOptions?.messageKey;
  return cv.IsInt({
    ...validationOptions,
    message,
    groups: validationOptions?.groups || ['error'],
  });
}

export function IsHtmlHead(
  validationOptions?: TI18nValidationOptions,
): PropertyDecorator {
  const key = validationOptions?.messageKey || 'validation.isHtmlHead';
  const message = () => translateErrorKey(key as never);
  if (validationOptions?.messageKey)
    message.key = validationOptions?.messageKey;

  return function (target: object, propertyName: string | symbol) {
    cv.registerDecorator({
      name: 'isHtmlHead',
      target: target.constructor,
      propertyName: propertyName as string,
      options: {
        ...validationOptions,
        groups: validationOptions?.groups || ['error'],
      },
      validator: {
        validate(value: unknown) {
          if (!value || typeof value !== 'string') {
            return true; // Empty value is allowed (IsOptional will handle it)
          }

          // Verify this is valid HTML for insertion into <head>
          const htmlContent = value.trim();

          // Basic HTML structure check
          if (!isValidHtmlForHead(htmlContent)) {
            return false;
          }

          return true;
        },
        defaultMessage: () =>
          message() || 'HTML contains invalid tags for head section',
      },
    });
  };
}

function isValidHtmlForHead(html: string): boolean {
  // Trim possible leading/trailing whitespace
  const trimmedHtml = html.trim();

  // An empty string is valid
  if (!trimmedHtml) {
    return true;
  }

  try {
    // Allowed tags for <head>
    const allowedTags = ['script', 'style', 'link', 'meta', 'title', 'base'];

    // Find all HTML tags
    const tagMatches =
      trimmedHtml.match(/<\/?([a-zA-Z][a-zA-Z0-9]*)[^>]*>/g) || [];

    for (const match of tagMatches) {
      const tagName = match
        .match(/<\/?([a-zA-Z][a-zA-Z0-9]*)/)?.[1]
        ?.toLowerCase();

      // Skip comments
      if (match.startsWith('<!--') && match.endsWith('-->')) {
        continue;
      }

      // Verify the tag is allowed
      if (!tagName || !allowedTags.includes(tagName)) {
        return false;
      }

      // Verify basic tag structure
      if (!isValidTagStructure(match)) {
        return false;
      }
    }

    // Verify all opening tags with closing counterparts (except self-closing) are paired
    return checkTagPairing(trimmedHtml);
  } catch {
    // If HTML parsing fails, treat as invalid
    return false;
  }
}

function isValidTagStructure(tag: string): boolean {
  // Basic tag structure check
  const hasOpeningBracket = tag.startsWith('<');
  const hasClosingBracket = tag.endsWith('>');

  if (!hasOpeningBracket || !hasClosingBracket) {
    return false;
  }

  // Verify there are no unclosed quotes
  const quotesCount = (tag.match(/"/g) || []).length;
  if (quotesCount % 2 !== 0) {
    return false;
  }

  // Verify there are no unclosed parentheses in attributes
  const openBrackets = (tag.match(/\(/g) || []).length;
  const closeBrackets = (tag.match(/\)/g) || []).length;
  if (openBrackets !== closeBrackets) {
    return false;
  }

  return true;
}

function checkTagPairing(html: string): boolean {
  const tagStack: string[] = [];
  const selfClosingTags = ['link', 'meta', 'base'];

  const tagMatches = html.match(/<\/?([a-zA-Z][a-zA-Z0-9]*)[^>]*>/g) || [];

  for (const match of tagMatches) {
    // Skip comments
    if (match.startsWith('<!--') && match.endsWith('-->')) {
      continue;
    }

    const isClosingTag = match.startsWith('</');
    const tagName = match
      .match(/<\/?([a-zA-Z][a-zA-Z0-9]*)/)?.[1]
      ?.toLowerCase();

    if (!tagName) {
      return false;
    }

    if (isClosingTag) {
      // Closing tag must match the most recent opening tag
      const lastOpenTag = tagStack.pop();
      if (lastOpenTag !== tagName) {
        return false;
      }
    } else {
      const isSelfClosing =
        match.endsWith('/>') || selfClosingTags.includes(tagName);

      if (!isSelfClosing) {
        // Opening tag — push onto the stack
        tagStack.push(tagName);
      }
    }
  }

  // All opening tags must be closed
  return tagStack.length === 0;
}

export function IsDateString(
  options?: ValidatorJS.IsISO8601Options,
  validationOptions?: TI18nValidationOptions,
): PropertyDecorator {
  const key = validationOptions?.messageKey || 'validation.isDateString';
  const message = () => translateErrorKey(key as never);
  if (validationOptions?.messageKey)
    message.key = validationOptions?.messageKey;
  return cv.IsDateString(options, {
    ...validationOptions,
    message,
    groups: validationOptions?.groups || ['error'],
  });
}

const { validate, ValidateIf, validateSync } = cv;

export { ValidateIf, validateSync, validate };
